import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect } from "react";
import { Modal, Box, Typography, Button, Divider, Alert } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { apiReporteResumenVentasPeriodo } from "../../settings/ApiConfig";
import { useRequest } from "../../context/System/RequestContext";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";

// Reporte pesado: mismo margen que ModalInventarioExistente (180s).
// Sin timeout explícito PostFileRequest aborta a los 60s (RequestContext createTimeoutSignal)
// y se pierde el PDF. Ventana bloqueada mientras genera.
const TIMEOUT_REPORTE_PESADO = 180000;

const modalStyle = {
    position: 'fixed',
    top: '5%',
    left: '30%',
    width: '40vw',
    maxWidth: '90vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

export default function ModalResumenVentasPeriodo(props) {
    const { openModal = false, onClose, caja } = props;

    const { PostFileRequest } = useRequest();

    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleOpenAlert } = useLayout();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [fechaInicio, setFechaInicio] = useState(new Date());
    const [fechaFin, setFechaFin] = useState(new Date());
    const [descripcionFiltro, setDescripcionFiltro] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            const hoy = new Date();
            setFechaFin(hoy);
            setTimeout(() => setModalVisible(true), 50);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    const getFechaLocal = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleGenerarReporte = async () => {
        if (fechaFin < fechaInicio) {
            handleOpenAlert("La fecha final no puede ser anterior a la fecha inicial", "warning");
            return;
        }

        setLoading(true);
        setLoadingMessage("Generando PDF a detalle, no cierre esta ventana...");
        try {
            const pcValue = caja ?? '';
            const data = {
                fechainicio: getFechaLocal(fechaInicio),
                fechafin: getFechaLocal(fechaFin),
                sucursal: parametrosCajaForm?.sucursal ?? '',
                usuario: usuarioData?.Usuario ?? '',
                tipousuario: "ADMINISTRADOR",
                pc: pcValue,
                descripcion: descripcionFiltro.trim() || '',
            };

            // JWE/JWT: se conserva PostFileRequest (inyecta Bearer + maneja blob).
            // Solo se agrega timeout largo para reportes pesados.
            const result = await PostFileRequest({
                url: apiReporteResumenVentasPeriodo,
                body: data,
                filename: `ResumenVentasPeriodo-${getFechaLocal(fechaInicio)}-${getFechaLocal(fechaFin)}.pdf`,
                openInNewTab: true,
                loader: "Generando PDF...",
                timeout: TIMEOUT_REPORTE_PESADO,
            });

            if (!result) throw new Error("No se pudo generar el reporte");

            handleOpenAlert("Reporte generado exitosamente", "success");
            onClose();
        } catch (error) {
            handleOpenAlert("Error al generar reporte: " + error.message, "error");
        } finally {
            setLoading(false);
            setLoadingMessage("");
        }
    };

    // Ventana bloqueada mientras se procesa el reporte pesado.
    const handleIntentarCerrar = () => {
        if (loading) return;
        onClose();
    };

    return (
        <Fragment>
        <Modal
            open={internalOpen}
            onClose={handleIntentarCerrar}
            closeAfterTransition
            disableEscapeKeyDown={loading}
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Resumen de Ventas por Periodo
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Resumen de ventas:</strong> Genera un reporte con el desglose diario de ventas por tipo de pago
                            (efectivo, tarjeta débito, tarjeta crédito, transferencia, devoluciones, apartados)
                            y el detalle de todas las ventas del periodo seleccionado.
                        </Typography>
                    </Alert>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Filtrar por descripción de artículo  <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(opcional)</Typography>
                        </Typography>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            placeholder="Ej: Camisa, Pantalón, etc."
                            value={descripcionFiltro}
                            onChange={(e) => setDescripcionFiltro(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Seleccionar rango de fechas del reporte:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Inicio</Typography>
                                <DatePicker
                                    selected={fechaInicio}
                                    onChange={(date) => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Fin</Typography>
                                <DatePicker
                                    selected={fechaFin}
                                    onChange={(date) => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                    minDate={fechaInicio}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleIntentarCerrar}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerarReporte}
                            disabled={loading}
                        >
                            {loading ? "Generando..." : "Generar Reporte PDF"}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
        <SizeSoftXLoadingProcess
            open={loading}
            message={loadingMessage ? `${loadingMessage} No cierre esta ventana.` : "Procesando reporte, por favor espere..."}
        />
        </Fragment>
    );
}
